import * as React from 'react'
import { ReactElement, useMemo, useRef, useState } from 'react'
import { IList, MarqueeSelection, MessageBar, MessageBarType, Selection, SelectionZone, Stack } from '@fluentui/react'
import { Link } from 'react-router-dom'
import { DocumentOverviewItemDto, DocumentSortOrder, IRootOverviewItemDto, isFolder } from '../../config/documentConfig'
import { DataPromise, getDataEmptyState, GraphError } from '../../context/AxiosContext'
import DocumentList from '../../components/List/Document/DocumentList'
import { IFileDto } from '../../config/fileConfig'
import { MainDto } from '../../config/filePartyConfig'
import { ISelection, SelectionMode } from '@fluentui/react/lib/Selection'
import { getChildCount } from '../../components/List/Document/FolderCell'
import DocumentSearchBox from '../../components/List/Document/DocumentSearchBox'
import DocumentCommandBar from '../../components/CommandBar/Document/DocumentCommandBar'
import { useForceUpdate } from '@fluentui/react-hooks'
import { useApplicationStyles } from '../App'
import { useTranslation } from 'react-i18next'

type Props = {
    file: IFileDto
    parties: MainDto[]
    documents: DataPromise<IRootOverviewItemDto>
    reset: () => void // Callback for when the view needs to be updated.
}

const flatMapItem = (item: DocumentOverviewItemDto): DocumentOverviewItemDto[] => [
    item,
    ...((isFolder(item) && item.children?.flatMap(flatMapItem)) || []),
]

const onSelectionChanged = (selection: ISelection<DocumentOverviewItemDto>, forceUpdate: () => void) => {
    if (selection.getSelectedCount() > 1) {
        // Deselect all folders when we're trying to select more than one item.
        selection.setChangeEvents(false, true)
        selection
            .getSelection()
            .filter(isFolder)
            .forEach(item => selection.setKeySelected(item.id, false, true))
        selection.setChangeEvents(true, false)
    }

    forceUpdate()
}

export default function DocumentOverview(props: Props): ReactElement {
    const forceUpdate = useForceUpdate()
    const { t: translate } = useTranslation()
    const applicationStyles = useApplicationStyles()

    const [searchResults, setSearchResults] = useState<DataPromise<string[], undefined, GraphError>>(getDataEmptyState())
    const [sortOrder, setSortOrder] = useState(DocumentSortOrder.ModifiedDesc) //set ModifiedDesc as default value
    const ref = useRef<IList>()
    const isLoading = useMemo<boolean>(() => getChildCount(props.documents.data?.children) === undefined, [props.documents.data?.children])
    const items = useMemo<DocumentOverviewItemDto[]>(
        () => (!isLoading && props.documents.data?.children?.flatMap(flatMapItem)) || [],
        [props.documents.data?.children, isLoading],
    )
    const selection = useMemo(
        () =>
            new Selection<DocumentOverviewItemDto>({
                items,
                onSelectionChanged: () => {
                    onSelectionChanged(selection, () => {
                        ref.current?.forceUpdate()
                        forceUpdate()
                    })
                },
                getKey: item => item.id,
                selectionMode: SelectionMode.multiple,
            }),
        [items, ref.current],
    )

    if (props.documents?.rejected && props.documents.error.response?.status === 404) {
        return (
            <MessageBar messageBarType={MessageBarType.warning}>
                {translate('file.pivot.document.error.not_found')}
                <Link to={`/file/${props.file.id}/edit`}>{translate('file.pivot.document.error.not_found.edit')}</Link>
            </MessageBar>
        )
    }

    if (props.documents?.rejected) {
        return <MessageBar messageBarType={MessageBarType.error}>{translate('file.pivot.document.error.no_access')}</MessageBar>
    }

    return (
        <Stack>
            <Stack className={applicationStyles.actionItems}>
                <DocumentSearchBox file={props.file} rootFolder={props.documents} setSearchResults={setSearchResults} />
                <DocumentCommandBar
                    selection={selection}
                    isLoading={isLoading}
                    file={props.file}
                    parties={props.parties}
                    rootFolder={props.documents}
                    sortOrder={sortOrder}
                    setSortOrder={setSortOrder}
                    reset={props.reset}
                />
            </Stack>
            <MarqueeSelection selection={selection as ISelection} isEnabled={true}>
                <SelectionZone selection={selection as ISelection}>
                    <DocumentList
                        componentRef={ref}
                        selection={selection}
                        isLoading={isLoading}
                        rootFolder={props.documents}
                        items={items}
                        searchResults={searchResults}
                        reset={props.reset}
                    />
                </SelectionZone>
            </MarqueeSelection>
        </Stack>
    )
}
